<template>
  <div class="yhc-service-level-delivery-dates">
    <div
      class="yhc-service-level-delivery-dates__wrapper"
      :class="{
        'yhc-service-level-delivery-dates__wrapper--single-column': !isTwoColumnLayout,
      }"
    >
      <!-- お届け日指定モード：未選択時 -->
      <div
        v-if="isDeliveryDateMode && !deliverySelectedDate"
        class="yhc-service-level-delivery-dates__no-dates yhc-service-level-delivery-dates__no-dates--full-width"
      >
        ※お届け日を選択してください
      </div>

      <!-- お届け日指定モード：お預かり可能日表示 -->
      <template v-else-if="isDeliveryDateMode && deliverySelectedDate">
        <!-- 当日自発着が利用可能な場合に表示（お預かり日選択時と同じ注釈）（法人向けでは非表示） -->
        <div
          v-if="!isFleaMarket && !isCorporationView && enableSameDayDelivery"
          class="same-day-delivery-notice"
        >
          <div class="same-day-delivery-notice__text">
            ※お預かり日当日のお届けが可能です。
            <a
              href="https://www.008008.jp/faq/?ul_select_id=deaf51ddff1d&ul_select_sid=7329193"
              target="_blank"
              rel="noopener noreferrer"
              class="same-day-delivery-notice__link"
            >
              詳しくはこちら
            </a>
          </div>
        </div>
        <!-- Eランク以下のお預かり可能日 -->
        <div
          v-if="showERankGroup && !isFleaMarket"
          class="yhc-service-level-delivery-dates__group"
        >
          <div class="yhc-service-level-delivery-dates__label">
            <span>Eランク以下</span>
            <span
              v-if="leadTimeERevisionNote"
              class="yhc-service-level-delivery-dates__revision-note"
            >
              {{ leadTimeERevisionNote }}
            </span>
          </div>
          <ul
            v-if="allPickupDatesAvailable.length > 0"
            class="yhc-service-level-delivery-dates__dates-list"
          >
            <li
              v-for="(date, index) in allPickupDatesAvailable"
              :key="index"
              class="yhc-service-level-delivery-dates__date-item"
            >
              {{ formatDateForDisplay(date) }}
            </li>
          </ul>
          <div
            v-else
            class="yhc-service-level-delivery-dates__no-dates"
          >
            該当するお預かり日はありません
          </div>
          <div
            v-if="isRemoteIsland && allPickupDatesAvailable.length > 0"
            class="yhc-service-level-delivery-dates__remote-island-note"
          >
            <div class="text-danger">※上記日付までにお預かりする必要があります</div>
            <div class="text-danger">※詳細は担当店より連絡します</div>
          </div>
        </div>
        <!-- FGランクのお預かり可能日 -->
        <div
          v-if="showFgRankGroup"
          class="yhc-service-level-delivery-dates__group"
        >
          <div
            v-if="!isFleaMarket"
            class="yhc-service-level-delivery-dates__label"
          >
            <span>FGランク</span>
            <span
              v-if="leadTimeFgRevisionNote"
              class="yhc-service-level-delivery-dates__revision-note"
            >
              {{ leadTimeFgRevisionNote }}
            </span>
          </div>
          <template v-if="$yhcefs.$userInputData.isFgRankAvailable">
            <ul
              v-if="fgPickupDatesAvailable.length > 0"
              class="yhc-service-level-delivery-dates__dates-list"
            >
              <li
                v-for="(date, index) in fgPickupDatesAvailable"
                :key="index"
                class="yhc-service-level-delivery-dates__date-item"
              >
                {{ formatDateForDisplay(date) }}
              </li>
            </ul>
            <div
              v-else
              class="yhc-service-level-delivery-dates__no-dates"
            >
              該当するお預かり日はありません
            </div>
            <div
              v-if="isRemoteIsland && fgPickupDatesAvailable.length > 0"
              class="yhc-service-level-delivery-dates__remote-island-note"
            >
              <div class="text-danger">※上記日付までにお預かりする必要があります</div>
              <div class="text-danger">※詳細は担当店より連絡します</div>
            </div>
          </template>
          <div
            v-else
            class="yhc-service-level-delivery-dates__unavailable-note"
          >
            {{ fgRankUnavailableNoteText }}
          </div>
        </div>
      </template>

      <!-- お預かり日指定モード：お届け可能日表示（従来） -->
      <template v-else>
        <!-- 当日自発着が利用可能な場合に表示（法人向けでは非表示） -->
        <div
          v-if="!isFleaMarket && !isCorporationView && enableSameDayDelivery"
          class="same-day-delivery-notice"
        >
          <div class="same-day-delivery-notice__text">
            ※お預かり日当日のお届けが可能です。
            <a
              href="https://www.008008.jp/faq/?ul_select_id=deaf51ddff1d&ul_select_sid=7329193"
              target="_blank"
              rel="noopener noreferrer"
              class="same-day-delivery-notice__link"
            >
              詳しくはこちら
            </a>
          </div>
        </div>
        <!-- Eランク以下のお届け可能日 -->
        <div
          v-if="showERankGroup && !isFleaMarket"
          class="yhc-service-level-delivery-dates__group"
        >
          <div class="yhc-service-level-delivery-dates__label">
            <span>Eランク以下</span>
            <span
              v-if="leadTimeERevisionNote"
              class="yhc-service-level-delivery-dates__revision-note"
            >
              {{ leadTimeERevisionNote }}
            </span>
          </div>
          <ul
            v-if="allDeliveryDates.length > 0"
            class="yhc-service-level-delivery-dates__dates-list"
          >
            <li
              v-for="(date, index) in allDeliveryDates"
              :key="index"
              class="yhc-service-level-delivery-dates__date-item"
            >
              {{ formatDateForDisplay(date) }}
            </li>
          </ul>
          <div
            v-if="isRemoteIsland && allDeliveryDates.length > 0"
            class="yhc-service-level-delivery-dates__remote-island-note"
          >
            <div class="text-danger">※上記日付までにお届けします</div>
            <div class="text-danger">※詳細は担当店より連絡します</div>
          </div>
          <div
            v-else-if="allDeliveryDates.length === 0"
            class="yhc-service-level-delivery-dates__no-dates"
          >
            計算できません
          </div>
        </div>

        <!-- FGランクのお届け可能日 -->
        <div
          v-if="showFgRankGroup"
          class="yhc-service-level-delivery-dates__group"
        >
          <div
            v-if="!isFleaMarket"
            class="yhc-service-level-delivery-dates__label"
          >
            <span>FGランク</span>
            <span
              v-if="leadTimeFgRevisionNote"
              class="yhc-service-level-delivery-dates__revision-note"
            >
              {{ leadTimeFgRevisionNote }}
            </span>
          </div>
          <!-- FGランクが利用可能な場合 -->
          <template v-if="$yhcefs.$userInputData.isFgRankAvailable">
            <ul
              v-if="fgDeliveryDates.length > 0"
              class="yhc-service-level-delivery-dates__dates-list"
            >
              <li
                v-for="(date, index) in fgDeliveryDates"
                :key="index"
                class="yhc-service-level-delivery-dates__date-item"
              >
                {{ formatDateForDisplay(date) }}
              </li>
            </ul>
            <div
              v-if="isRemoteIsland && fgDeliveryDates.length > 0"
              class="yhc-service-level-delivery-dates__remote-island-note"
            >
              <div class="text-danger">※上記日付までにお届けします</div>
              <div class="text-danger">※詳細は担当店より連絡します</div>
            </div>
            <div
              v-else-if="fgDeliveryDates.length === 0"
              class="yhc-service-level-delivery-dates__no-dates"
            >
              計算できません
            </div>
          </template>
          <!-- FGランクが利用不可の場合の注釈 -->
          <div
            v-else
            class="yhc-service-level-delivery-dates__unavailable-note"
          >
            {{ fgRankUnavailableNoteText }}
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import { YhcEstimateFormFrontService } from '../../YhcEstimateFormFrontPage/YhcEstimateFormFrontService'
import { formatDateWithWeekday } from '../utils/dateFormatter'
import { listPickupDatesDeliveringBy } from '../ssSRankFeeNote'
import {
  resolveEffectiveRankDisplayMode,
  fgRankUnavailableNoteText as getFgRankUnavailableNoteText,
  type CorporationRankDisplayMode,
} from '../utils/yhcServiceLevelRankDisplayMode'

export default {
  name: 'YhcServiceLevelDeliveryDates',
  inject: ['YhcEstimateFormFrontServiceInstance'],
  props: {
    isCorporationView: {
      type: Boolean,
      default: false,
    },
    rankDisplayMode: {
      type: String as () => CorporationRankDisplayMode,
      default: 'both',
    },
    leadTimeERevisionNote: {
      type: String,
      default: '',
    },
    leadTimeFgRevisionNote: {
      type: String,
      default: '',
    },
  },
  computed: {
    $yhcefs(): YhcEstimateFormFrontService {
      return this.YhcEstimateFormFrontServiceInstance
    },
    effectiveRankDisplayMode(): CorporationRankDisplayMode {
      return resolveEffectiveRankDisplayMode(
        this.rankDisplayMode,
        this.isCorporationView,
      )
    },
    showERankGroup(): boolean {
      const m = this.effectiveRankDisplayMode
      return m === 'both' || m === 'e_only'
    },
    showFgRankGroup(): boolean {
      const m = this.effectiveRankDisplayMode
      return m === 'both' || m === 'fg_only'
    },
    isTwoColumnLayout(): boolean {
      return (
        this.showERankGroup &&
        this.showFgRankGroup &&
        !this.isFleaMarket
      )
    },
    fgRankUnavailableNoteText(): string {
      return getFgRankUnavailableNoteText(this.effectiveRankDisplayMode)
    },
    /** 発着＝お届け日指定モードか */
    isDeliveryDateMode(): boolean {
      return this.$yhcefs.$userInputData.serviceLevelDateMode === 'delivery'
    },
    /** お届け日指定モードで選択したお届け日（未選択時は null） */
    deliverySelectedDate(): string | null {
      return this.$yhcefs.$userInputData.deliverySelectedDate
    },
    /**
     * お届け日 D までに届くお預かり可能日（Eランク以下）を D-1～D-14 の範囲で降順に列挙（リードタイムを加味）。
     * 「その日お預かりすると D 以前にお届けになる」お預かり日を表示するため、delivery(P) <= D で判定する。
     * 14日前までに候補が0件の場合のみ、さらに遡って最初に見つかったお預かり可能日を1件だけ表示（0件表示を防ぐ）。
     */
    allPickupDatesAvailable(): string[] {
      return this.listPickupDatesForRank('sizeRank0NeedDays')
    },
    /**
     * お届け日 D までに届くお預かり可能日（FGランク）。同上で delivery(P) <= D で判定。
     * 14日前までに0件の場合のみ、さらに遡って最初に見つかったお預かり可能日を1件だけ表示。
     */
    fgPickupDatesAvailable(): string[] {
      return this.listPickupDatesForRank('sizeRank1NeedDays')
    },
    canCalcDate(): boolean {
      return (
        this.$yhcefs.$userInputData.deliveryDateDisplayLabel.indexOf('年') >= 0 &&
        !this.$yhcefs.$userInputData.pickupDateResetFlag &&
        !!this.$yhcefs.$userInputData.pickupSelectedDate
      )
    },
    isSameDayDelivery(): boolean {
      return this.$yhcefs.$userInputData.isSameDayPickupAndDelivery
    },
    isRemoteIsland(): boolean {
      return (
        this.$yhcefs.$userInputData.zip_from_yhc_master?.isRemoteIsland === true ||
        this.$yhcefs.$userInputData.zip_to_yhc_master?.isRemoteIsland === true
      )
    },
    // Eランク以下の最短お届け日
    earliestDeliveryDate(): string {
      return this.$yhcefs.$userInputData.deliveryDate
    },
    // FGランクの最短お届け日
    fgEarliestDeliveryDate(): string {
      return this.$yhcefs.$userInputData.fgDeliveryDate
    },
    // Eランク以下の全てのお届け可能日
    allDeliveryDates(): string[] {
      if (
        !this.$yhcefs.$userInputData.pref_from_to_info_master ||
        this.$yhcefs.$userInputData.deliveryDateDisplayErrorMessage ||
        this.isSameDayDelivery ||
        this.$yhcefs.$userInputData.pickupDateResetFlag
      ) {
        return this.earliestDeliveryDate ? [this.earliestDeliveryDate] : []
      }

      const pickupDate = this.$yhcefs.$userInputData.pickupSelectedDate
      if (!pickupDate) return []

      if (this.isRemoteIsland) {
        const adjustedDate = this.$yhcefs.$userInputData.remoteIslandDeliveryDateAdjusted
        if (adjustedDate) {
          return [adjustedDate]
        }
        const remoteIslandDate = $core
          .$dayjs(pickupDate)
          .add(14, 'day')
          .format('YYYY-MM-DD')
        return [remoteIslandDate]
      }

      const endDate = $core.$dayjs(pickupDate).add(14, 'day').format('YYYY-MM-DD')

      return this.calculateDeliveryDatesInPeriod(
        this.earliestDeliveryDate,
        endDate,
      )
    },
    // FGランクの全てのお届け可能日
    fgDeliveryDates(): string[] {
      if (
        !this.$yhcefs.$userInputData.pref_from_to_info_master ||
        this.$yhcefs.$userInputData.deliveryDateDisplayErrorMessage ||
        this.isSameDayDelivery ||
        this.$yhcefs.$userInputData.pickupDateResetFlag
      ) {
        return this.fgEarliestDeliveryDate ? [this.fgEarliestDeliveryDate] : []
      }

      const pickupDate = this.$yhcefs.$userInputData.pickupSelectedDate
      if (!pickupDate) return []

      if (this.isRemoteIsland) {
        const adjustedDate = this.$yhcefs.$userInputData.remoteIslandDeliveryDateAdjusted
        if (adjustedDate) {
          return [adjustedDate]
        }
        const remoteIslandDate = $core
          .$dayjs(pickupDate)
          .add(14, 'day')
          .format('YYYY-MM-DD')
        return [remoteIslandDate]
      }

      const endDate = $core.$dayjs(pickupDate).add(14, 'day').format('YYYY-MM-DD')

      return this.calculateDeliveryDatesInPeriod(
        this.fgEarliestDeliveryDate,
        endDate,
      )
    },
    isFleaMarket(): boolean {
      // フリマ用ではすべてFGランクのお届可能日となるため表示制御のために追加
      return this.$yhcefs.isFleaMarket
    },
    enableSameDayDelivery(): boolean {
      return (
        !this.$yhcefs.isFleaMarket &&
        this.$yhcefs.$userInputData.sameDayPickupAndDeliveryAvailable &&
        !this.$yhcefs.$userInputData.deliveryPeakSeasonRule?.sameDayDeliveryDisabled
      )
    },
  },
  methods: {
    /**
     * お届け日 D までに届くお預かり可能日を、D-1～D-14 の範囲で近い順に返す。
     * 14日前までに0件の場合のみ、さらに遡って最初の1件を返す。
     */
    listPickupDatesForRank(
      sizeRank: 'sizeRank0NeedDays' | 'sizeRank1NeedDays',
    ): string[] {
      const D = this.deliverySelectedDate
      if (!D || !this.$yhcefs.$userInputData.pref_from_to_info_master) return []
      return listPickupDatesDeliveringBy({
        deliveryDate: D,
        isWorkingDay: (ymd) => this.isFromWorkingDay(ymd),
        deliveryDateForPickup: (ymd) =>
          this.$yhcefs.$userInputData.computeDeliveryDateForPickupDate(ymd, sizeRank),
      })
    },
    formatDateForDisplay(dateStr: string): string {
      return formatDateWithWeekday(dateStr)
    },
    /** 指定日がお預かり先の営業日かどうか（お預かり可能日逆算用） */
    isFromWorkingDay(dateStr: string): boolean {
      return this.$yhcefs.$userInputData.isPickupWorkingDayForDate(dateStr)
    },
    calculateDeliveryDatesInPeriod(
      startDateStr: string,
      endDateStr: string,
    ): string[] {
      const results: string[] = []
      if (!startDateStr || !endDateStr) return results

      let currentDate = $core.$dayjs(startDateStr)
      const endDate = $core.$dayjs(endDateStr)

      if (currentDate.isAfter(endDate)) return results

      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate)) {
        const dateStr = currentDate.format('YYYY-MM-DD')
        if (this.$yhcefs.$userInputData.isDeliveryWorkingDayForDate(dateStr)) {
          results.push(dateStr)
        }
        currentDate = currentDate.add(1, 'day')
      }
      return results
    },
  },
}
</script>

<style lang="scss" scoped>
.yhc-service-level-delivery-dates {
  padding: 0;
  background-color: transparent;
  border-radius: 0;
  box-shadow: none;
  box-sizing: border-box;

  &__wrapper {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px;
    width: 100%;
    align-items: flex-start;

    &--single-column {
      grid-template-columns: 1fr;
    }
  }

  &__group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__label {
    font-size: 14px;
    font-weight: bold;
    color: var(--yhc-color, #1a73e8);
    margin-bottom: 5px;
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 0.35rem;
  }

  &__revision-note {
    font-weight: 600;
    font-size: 0.75rem;
    color: #dc3545;
    line-height: 1.3;
  }

  &__dates-list {
    display: flex;
    flex-direction: column; // 縦並びに固定
    gap: 6px;
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__date-item {
    font-size: 14px;
    padding: 4px 8px;
    background-color: #f8f9fa;
    border-radius: 4px;
    color: #333;
  }

  &__no-dates {
    font-size: 14px;
    color: #666;
    margin-top: 8px;

    &--full-width {
      grid-column: 1 / -1;
    }
  }

  &__unavailable-note {
    font-size: 14px;
    color: #dc3545;
    /* 赤系の色 */
    margin-top: 8px;
  }

  &__remote-island-note {
    font-size: 12px;
    color: #666;
    margin-top: 8px;
    line-height: 1.5;
  }
}

.same-day-delivery-notice {
  grid-column: 1 / -1;
  margin-top: 0.115rem;
  padding: 0.25rem 0.5rem;
  background-color: #e3f2fd;
  border-radius: 6px;
  border-left: 4px solid #1976d2;

  &__text {
    font-size: 14px;
    font-weight: 600;
    color: #1976d2;
  }

  &__additional {
    font-size: 13px;
    color: #333;
    display: flex;
    align-items: center;
    gap: 8px;
    flex-wrap: wrap;
  }

  &__link {
    color: #1976d2;
    text-decoration: underline;
    font-weight: 500;

    &:hover {
      color: #1565c0;
      text-decoration: none;
    }
  }
}

.yhc-service-level-delivery-dates {
  @media screen and (max-width: 767px) {
    width: 100%;

    &__wrapper {
      gap: 12px;
    }

    &__label {
      font-size: 14px;
    }

    &__date-item {
      font-size: 13px;
    }
  }
}

@media screen and (max-width: 767px) {
  .same-day-delivery-notice {
    &__text {
      font-size: 13px;
    }

    &__additional {
      font-size: 12px;
      gap: 6px;
    }

    &__link {
      font-size: 12px;
    }
  }
}
</style>
